import * as React from 'react';
import { Ellipsis, Plus } from 'lucide-react';
import { t, type MessageKey } from '@reos/i18n';
import { systemClock } from '@reos/config';
import { FieldRow, FormSection, SaveStatus, SplitEditorLayout, type SaveStatusKind } from '@redhand/ui';
import {
  createOrRetrySupplier,
  fetchSupplierLogo,
  fetchSupplierPreviewPdf,
  getBillingOverview,
  listSuppliers,
  lookupAresCompany,
  removeSupplierLogo,
  retrySupplierSync,
  setSupplierBankAccount,
  updateBillerMode,
  updateSupplierBrand,
  uploadSupplierLogo,
  useOfficeLogo,
  type AresCompanyDto,
  type BillerMode,
  type BillingOverviewDto,
  type LogoChoice,
  type ReadinessStepKey,
  type SupplierDto,
  type SupplierLogoDto,
  type SyncDelivery,
  type SyncJobDto,
} from '../lib/billing-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { focusField } from '../lib/form-errors.js';
import { readFileAsBase64 } from '../lib/file-data-url.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { CatalogSection, InvoicesOverview, NewInvoiceTab } from '../components/invoices.js';
import { CommissionPayoutsTab } from '../components/commission-payouts.js';
import { PageHead } from '../components/page-head.js';
import { useSession } from '../stores/session.js';

/**
 * `/fakturace` — jedna obrazovka (motor MyUcto, makler ho nevidi).
 *
 * ROZHODNUTI UXR2-8 (nahrazuje FAK2-4): predloha `05-fakturace.html` ma
 * JEDNU obrazovku — dlazdice, rychle filtry a seznam dokladu — a v hlavicce
 * jedno primarni tlacitko „Nová faktura". Zalozky „Přehled" a „Doklady" se
 * proto slily do jednoho pohledu (dlazdice jsou prehled a stoji nad seznamem,
 * ktery je vysvetluje), „Nová faktura" je primarni tlacitko a „Nastavení
 * fakturace" (vcetne druheho dodavatele z FAK5) je v nabidce „⋯". Zadna
 * funkce nezmizela, jen uz se neda otevrit prazdny „Přehled" bez cisel.
 */

/** Krok PROV1 pridal pohled `commissions` (prehled provizi a mesicni uzaverka). */
type View = 'list' | 'new' | 'settings' | 'commissions';

const LOCALE = 'cs' as const;

const READINESS_STEP_KEY: Readonly<Record<ReadinessStepKey, MessageKey>> = {
  company: 'webAdmin.fakturace.readiness.stepCompany',
  bank: 'webAdmin.fakturace.readiness.stepBank',
  brand: 'webAdmin.fakturace.readiness.stepBrand',
  firstInvoice: 'webAdmin.fakturace.readiness.stepFirstInvoice',
};

const STATUS_TITLE_KEY: Readonly<Record<BillingOverviewDto['status'], MessageKey>> = {
  unconfigured: 'webAdmin.fakturace.status.unconfigured.title',
  notStarted: 'webAdmin.fakturace.status.notStarted.title',
  preparing: 'webAdmin.fakturace.status.preparing.title',
  active: 'webAdmin.fakturace.status.active.title',
  failed: 'webAdmin.fakturace.status.failed.title',
};

const STATUS_DETAIL_KEY: Readonly<Record<BillingOverviewDto['status'], MessageKey>> = {
  unconfigured: 'webAdmin.fakturace.status.unconfigured.detail',
  notStarted: 'webAdmin.fakturace.status.notStarted.detail',
  preparing: 'webAdmin.fakturace.status.preparing.detail',
  active: 'webAdmin.fakturace.status.active.detail',
  failed: 'webAdmin.fakturace.status.failed.detail',
};

/**
 * Strojove kody problem+json -> veta ze slovniku. Krok FAK4 pridal kody loga
 * (`finance/logo-*`) a "branding kancelare logo nema" - uzivatel ma dostat
 * duvod, ne holy kod.
 */
const PROBLEM_REASON_KEY: Readonly<Record<string, MessageKey>> = {
  'finance/bank-account-empty': 'webAdmin.fakturace.settings.bankAccountReasonEmpty',
  'finance/bank-account-shape': 'webAdmin.fakturace.settings.bankAccountReasonShape',
  'finance/bank-account-mod11': 'webAdmin.fakturace.settings.bankAccountReasonMod11',
  'finance/bank-account-iban': 'webAdmin.fakturace.settings.bankAccountReasonIban',
  'finance/logo-empty': 'webAdmin.fakturace.settings.brandLogoReasonEmpty',
  'finance/logo-format': 'webAdmin.fakturace.settings.brandLogoReasonFormat',
  'finance/logo-too-large': 'webAdmin.fakturace.settings.brandLogoReasonTooLarge',
  'finance/logo-unsafe-svg': 'webAdmin.fakturace.settings.brandLogoReasonUnsafeSvg',
  'finance/office-logo-unavailable': 'webAdmin.fakturace.settings.brandLogoOfficeUnavailable',
  'finance/supplier-preview-unavailable': 'webAdmin.fakturace.settings.previewUnavailable',
};

function errorMessage(error: unknown, fallbackKey: MessageKey): string {
  if (error instanceof DomainApiError) {
    const mapped = error.type !== undefined ? PROBLEM_REASON_KEY[error.type] : undefined;
    return mapped !== undefined ? t(mapped, { locale: LOCALE }) : error.message;
  }
  return t(fallbackKey, { locale: LOCALE });
}

/**
 * Krok FAK4: vysledek odeslani do MyUcta jako stav formulare. `queued` NENI
 * uspech ("odeslano") ani chyba - je to "ulozeno, odesle se" a musi mit vlastni
 * vetu, jinak se vrati presne ten tichy stav, ktery FAK4 opravuje.
 */
interface DeliveryView {
  readonly status: SaveStatusKind;
  readonly savedText: string;
  readonly errorDetail: string | undefined;
  readonly canRetry: boolean;
}

function describeDelivery(delivery: SyncDelivery, kind: 'brand' | 'bank'): DeliveryView {
  const key = (brand: MessageKey, bank: MessageKey): MessageKey => (kind === 'brand' ? brand : bank);
  if (delivery.state === 'applied') {
    return {
      status: 'saved',
      savedText: t(key('webAdmin.fakturace.settings.deliveryApplied', 'webAdmin.fakturace.settings.bankDeliveryApplied'), { locale: LOCALE }),
      errorDetail: undefined,
      canRetry: false,
    };
  }
  if (delivery.state === 'queued') {
    return {
      status: 'saved',
      savedText: t(
        delivery.reason === 'not_connected'
          ? key('webAdmin.fakturace.settings.deliveryQueuedNotConnected', 'webAdmin.fakturace.settings.bankDeliveryQueuedNotConnected')
          : key('webAdmin.fakturace.settings.deliveryQueuedRetry', 'webAdmin.fakturace.settings.bankDeliveryQueuedNotConnected'),
        { locale: LOCALE },
      ),
      errorDetail: undefined,
      canRetry: delivery.reason === 'retry',
    };
  }
  return {
    status: 'error',
    savedText: '',
    errorDetail: t(key('webAdmin.fakturace.settings.brandApplyError', 'webAdmin.fakturace.settings.bankDeliveryError'), {
      locale: LOCALE,
      values: { error: delivery.error },
    }),
    canRetry: true,
  };
}

/**
 * Stav fronty ze `overview` jako VYCHOZI stav formulare - aby "nepodarilo se
 * odeslat" a tlacitko "Zkusit znovu" prezily reload stranky. Bez toho by
 * uzivatel po F5 videl cisty formular a neodeslany vzhled by zmizel z ocí.
 * Uloha ve stavu `error` uz vycerpala pokusy a worker ji sam nevezme - tam je
 * rucni opakovani jediná cesta dal.
 */
function deliveryFromSyncJob(job: SyncJobDto | undefined): SyncDelivery | null {
  if (job === undefined || job.status === 'done') return null;
  if (job.lastError !== null) return { state: 'failed', error: job.lastError };
  return { state: 'queued', reason: job.status === 'error' ? 'retry' : 'not_connected' };
}

/** `SaveStatus` je `exactOptionalPropertyTypes` - `errorDetail` se nesmi poslat jako `undefined`. */
function errorDetailProp(value: string | undefined): { errorDetail?: string } {
  return value === undefined ? {} : { errorDetail: value };
}

/**
 * Zivy pruchod 13. 10. (V7): hlasky „nastavte v Nastavení" (fakturace,
 * menu avatara) na spravny formular odkud nevedly - makler se dostal jen
 * na hlavni list fakturace, ne primo na formular udaju firmy. Odkaz
 * `/fakturace?view=settings` ted zalozku rovnou otevre.
 */
function initialViewFromLocation(): View {
  if (typeof window === 'undefined') return 'list';
  const view = new URLSearchParams(window.location.search).get('view');
  // Krok PROV1: `?view=commissions` otevre prehled provizi rovnou (odkaz
  // z uzaverky pro uctarnu i z hlasek), stejne jako `?view=settings`.
  if (view === 'settings') return 'settings';
  return view === 'commissions' ? 'commissions' : 'list';
}

export function FakturacePage(): React.JSX.Element {
  const [view, setView] = React.useState<View>(initialViewFromLocation);
  /** Krok D1 bod 2: ukazkova kancelar (`GET /api/me` → `flags.demo`). */
  const demo = useSession((state) => state.demo);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [overview, setOverview] = React.useState<BillingOverviewDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setOverview(await getBillingOverview());
    } catch (error) {
      setLoadError(error instanceof DomainApiError ? error.message : t('webAdmin.fakturace.settings.notConfigured', { locale: LOCALE }));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const month = systemClock.now().toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
  const supplierName = overview?.supplier?.companyName ?? null;

  const title =
    view === 'new'
      ? t('webAdmin.fakturace.tabNewInvoice', { locale: LOCALE })
      : view === 'settings'
        ? t('webAdmin.fakturaceV2.tabSettings', { locale: LOCALE })
        : view === 'commissions'
          ? t('webAdmin.provize.title', { locale: LOCALE })
          : t('webAdmin.fakturace.title', { locale: LOCALE });

  return (
    <div data-testid="fakturace-page">
      <PageHead
        title={title}
        {...(view === 'list'
          ? {
              subtitle:
                supplierName === null
                  ? t('webAdmin.fakturaceV2.subtitleNoSupplier', { locale: LOCALE, values: { month } })
                  : t('webAdmin.fakturaceV2.subtitle', { locale: LOCALE, values: { month, supplier: supplierName } }),
            }
          : {
              breadcrumb: (
                <button type="button" className="rh-a2-link" onClick={() => setView('list')} data-testid="fakturace-back">
                  {t('webAdmin.fakturace.title', { locale: LOCALE })}
                </button>
              ),
            })}
        {...(view === 'list'
          ? {
              action: (
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--primary"
                  onClick={() => setView('new')}
                  data-testid="fakturace-new-invoice"
                >
                  <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                    <Plus size={15} strokeWidth={2} />
                  </span>
                  {t('webAdmin.fakturaceDoklady.newInvoiceButton', { locale: LOCALE })}
                </button>
              ),
            }
          : {})}
        menu={
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="rh-a2-dots"
              aria-label={t('webAdmin.zakazky.rowMenuLabel', { locale: LOCALE })}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              data-testid="fakturace-more-menu"
            >
              <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                <Ellipsis size={15} strokeWidth={1.75} />
              </span>
            </button>
            {menuOpen ? (
              <div className="rh-a2-menu__pop" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)' }}>
                <button
                  type="button"
                  className="rh-a2-menu__row"
                  onClick={() => { setMenuOpen(false); setView('settings'); }}
                  data-testid="fakturace-open-settings"
                >
                  {t('webAdmin.fakturaceV2.tabSettings', { locale: LOCALE })}
                </button>
                {/*
                  Krok PROV1: prehled provizi stoji v „⋯" vedle Nastaveni, ne
                  jako nova polozka leveho menu — `nav.spec.ts` je chranene
                  meridlo na slozeni skupiny „Penize".
                */}
                <button
                  type="button"
                  className="rh-a2-menu__row"
                  onClick={() => { setMenuOpen(false); setView('commissions'); }}
                  data-testid="fakturace-open-commissions"
                >
                  {t('webAdmin.provize.title', { locale: LOCALE })}
                </button>
                {view !== 'list' ? (
                  <button
                    type="button"
                    className="rh-a2-menu__row"
                    onClick={() => { setMenuOpen(false); setView('list'); }}
                  >
                    {t('webAdmin.fakturace.title', { locale: LOCALE })}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        }
      />

      <div className="rh-a2-page rh-a2-page--wide">
        {loading && <p role="status" className="rh-a2-status">…</p>}
        {loadError !== null && (
          <p role="alert" className="rh-a2-error">
            {loadError}
          </p>
        )}

        {!loading && loadError === null && overview !== null && (
          <>
            {/*
              Stav zapojeni fakturace a prubeh nastaveni se ukazuji JEN dokud
              nejsou hotove — na rozjete kancelari obrazovka vypada jako
              predloha (dlazdice + seznam), ne jako onboarding.
            */}
            {/*
              Krok D1 bod 2: v UKAZKOVE kancelari se doklady nevystavuji
              (serverovy zakaz v `issueInvoice`), takze onboarding fakturace
              tady nema co nabizet — zustaval viset v nedokoncenem stavu a
              vypadal jako zaseknute nastaveni. Misto nej stoji vysvetleni.
            */}
            {view === 'list' && demo && <DemoAccountingCard />}
            {view === 'list' && !demo && overview.status !== 'active' && (
              <BillingStatusCard overview={overview} onGoToSettings={() => setView('settings')} />
            )}
            {view === 'list' && !demo && !overview.readiness.complete && (
              <BillingReadinessCard overview={overview} onGoToSettings={() => setView('settings')} />
            )}
            {view === 'list' && <InvoicesOverview />}
            {view === 'new' && (
              <NewInvoiceTab
                supplierId={overview.supplier?.id ?? null}
                vatPayer={overview.supplier?.vatPayer ?? true}
                onCancel={() => setView('list')}
              />
            )}
            {view === 'settings' && <SettingsTab overview={overview} onChanged={reload} />}
            {view === 'commissions' && <CommissionPayoutsTab />}
          </>
        )}
      </div>
    </div>
  );
}

function BillingStatusCard({
  overview,
  onGoToSettings,
}: {
  readonly overview: BillingOverviewDto;
  readonly onGoToSettings: () => void;
}): React.JSX.Element {
  return (
    <section className="rh-a2-card" data-testid="billing-status-banner" style={{ marginBottom: 'var(--rh-space-5)' }}>
      <div className="rh-a2-card__head">
        <h2>{t(STATUS_TITLE_KEY[overview.status], { locale: LOCALE })}</h2>
      </div>
      <p style={{ margin: 0 }}>{t(STATUS_DETAIL_KEY[overview.status], { locale: LOCALE })}</p>
      {/* Zivy pruchod 13. 10. (V7): hlaska „vyplňte v Nastavení" nikam
          nevedla — primy odkaz na formular udaju firmy primo odtud. */}
      <button
        type="button"
        className="rh-a2-btn"
        style={{ marginTop: 12 }}
        onClick={onGoToSettings}
        data-testid="billing-status-cta"
      >
        {t('webAdmin.fakturace.settings.submitButton', { locale: LOCALE })}
      </button>
    </section>
  );
}

/**
 * Krok D1 bod 2 — proc je fakturace v ukazce vypnuta.
 * Krok UXF3 bod 6 (#5): uz to NENI karta s nadpisem, ale jeden tenky pruh
 * (`.rh-a2-demobar--quiet`) — souhrn a seznam faktur maji byt hned pod
 * titulkem, ne pod stavem nastaveni.
 */
function DemoAccountingCard(): React.JSX.Element {
  return (
    <p className="rh-a2-demobar rh-a2-demobar--quiet" data-testid="billing-demo-card">
      <strong>{t('webAdmin.demoMode.accountingTitle', { locale: LOCALE })}</strong>{' '}
      {t('webAdmin.demoMode.accountingText', { locale: LOCALE })}
    </p>
  );
}

function BillingReadinessCard({
  overview,
  onGoToSettings,
}: {
  readonly overview: BillingOverviewDto;
  readonly onGoToSettings: () => void;
}): React.JSX.Element {
  const readiness = overview.readiness;
  return (
    <section className="rh-a2-card" data-testid="billing-readiness-card" style={{ marginBottom: 'var(--rh-space-5)' }}>
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.fakturace.readiness.title', { locale: LOCALE, values: { percent: readiness.percent } })}</h2>
      </div>
      {/*
        Krok UXD4 (nalez V6 UX auditu 21. 10.) — STAV POLOZKY NEJDE JEN
        BARVOU TECKY. Pri „hotovo 75 %" vypadaly vsechny ctyri polozky stejne
        (8px puntik) a makléř nevedel, KTERA jedna jeste chybi, dokud kazdou
        nerozklikl. Stav proto nese i slovo (WCAG 1.4.1 — informace nesmi
        stat jen na barve); `data-done` je totéz pro sadu.
      */}
      <ul className="rh-a2-rows">
        {readiness.steps.map((step) => (
          <li key={step.key} data-testid={`billing-readiness-step-${step.key}`} data-done={step.done ? 'yes' : 'no'}>
            <span className={`rh-a2-dot ${step.done ? 'rh-a2-dot--ok' : ''}`} aria-hidden="true" />
            <span className="rh-a2-grow">
              <span className="rh-a2-title">{t(READINESS_STEP_KEY[step.key], { locale: LOCALE })}</span>
            </span>
            <span className={`rh-a2-badge ${step.done ? 'rh-a2-badge--ok' : ''}`}>
              {t(step.done ? 'webAdmin.fakturace.readiness.stepDone' : 'webAdmin.fakturace.readiness.stepOpen', {
                locale: LOCALE,
              })}
            </span>
          </li>
        ))}
      </ul>
      {readiness.open !== null && readiness.open !== 'firstInvoice' && (
        <button type="button" className="rh-a2-btn" style={{ marginTop: 12 }} onClick={onGoToSettings} data-testid="billing-readiness-cta">
          {t('webAdmin.fakturace.settings.submitButton', { locale: LOCALE })}
        </button>
      )}
    </section>
  );
}

interface CompanyForm {
  ico: string;
  companyName: string;
  street: string;
  city: string;
  zip: string;
  dic: string;
  vatPayer: boolean;
  email: string;
}

const EMPTY_COMPANY_FORM: CompanyForm = { ico: '', companyName: '', street: '', city: '', zip: '', dic: '', vatPayer: true, email: '' };

function SettingsTab({ overview, onChanged }: { readonly overview: BillingOverviewDto; readonly onChanged: () => Promise<void> }): React.JSX.Element {
  // FIX7 nalez 2/9d: udaje firmy aktivniho dodavatele kanceláře drive sly
  // jen cist (`SettingsTab` u `status==='active'` vzdy ukazovala needitovatelny
  // <p>) - spatne zadane ICO tak nemelo cestu ven. Editace se odhali tlacitkem
  // „Upravit" (stejny vzor jako „second-supplier-edit-" u druheho dodavatele),
  // aby se neukazoval cely formular hned, kdyz uz je dodavatel aktivni a nic
  // se menit nema.
  const [editingCompany, setEditingCompany] = React.useState(false);
  return (
    <div className="flex flex-col gap-4">
      <BillerModeSection billerMode={overview.billerMode} onChanged={onChanged} />
      {overview.supplier === null || overview.supplier.status === 'error' || editingCompany ? (
        <CompanySection
          existing={overview.supplier}
          brokerUserId={null}
          onChanged={async () => {
            setEditingCompany(false);
            await onChanged();
          }}
          {...(overview.supplier?.status === 'active'
            ? { onCancel: () => setEditingCompany(false) }
            : {})}
        />
      ) : (
        <FormSection title={t('webAdmin.fakturace.settings.companyHeading', { locale: LOCALE })}>
          <p className="text-sm text-gray-600">
            {overview.supplier.companyName} — {overview.supplier.ico}
          </p>
          <button
            type="button"
            className="rh-btn"
            onClick={() => setEditingCompany(true)}
            data-testid="company-edit-button"
          >
            {t('webAdmin.fakturace.settings.companyEditButton', { locale: LOCALE })}
          </button>
        </FormSection>
      )}
      {overview.supplier !== null && (
        <BankSection
          supplier={overview.supplier}
          qrText={overview.bankQrText}
          syncJob={overview.syncJobs.find((job) => job.kind === 'bank')}
          onChanged={onChanged}
        />
      )}
      {overview.supplier !== null && (
        <BrandSection
          supplierId={overview.supplier.id}
          brand={overview.brand}
          companyName={overview.supplier.companyName}
          isBrokerSupplier={overview.supplier.brokerUserId !== null}
          logo={overview.logo}
          inheritedFrom={overview.brandInheritedFrom}
          officeLogoAvailable={overview.officeLogoAvailable}
          syncJob={overview.syncJobs.find((job) => job.kind === 'brand')}
          onChanged={onChanged}
        />
      )}
      <CatalogSection />
      <SecondSupplierSection />
    </div>
  );
}

function BillerModeSection({ billerMode, onChanged }: { readonly billerMode: BillerMode; readonly onChanged: () => Promise<void> }): React.JSX.Element {
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);

  async function change(mode: BillerMode): Promise<void> {
    if (mode === billerMode) return;
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      await updateBillerMode(mode);
      setStatus('saved');
      await onChanged();
    } catch (error) {
      setStatus('error');
      setErrorDetail(error instanceof DomainApiError ? error.message : undefined);
    }
  }

  return (
    <FormSection
      title={t('webAdmin.fakturace.settings.billerModeLabel', { locale: LOCALE })}
      footer={<SaveStatus status={status} texts={{ saving: '…', saved: t('webAdmin.fakturace.settings.billerModeSaved', { locale: LOCALE }), error: '' }} {...errorDetailProp(errorDetail)} />}
    >
      <FieldRow label={t('webAdmin.fakturace.settings.billerModeLabel', { locale: LOCALE })}>
        <select className="rh-input block" value={billerMode} onChange={(e) => void change(e.target.value as BillerMode)} data-testid="biller-mode-select">
          <option value="office">{t('webAdmin.fakturace.settings.billerModeOffice', { locale: LOCALE })}</option>
          <option value="broker">{t('webAdmin.fakturace.settings.billerModeBroker', { locale: LOCALE })}</option>
        </select>
      </FieldRow>
    </FormSection>
  );
}

function CompanySection({
  existing,
  brokerUserId,
  onChanged,
  onCancel,
}: {
  readonly existing: BillingOverviewDto['supplier'];
  /** Krok FAK5 bod 3 - nenull, kdyz admin zaklada dodavatele KONKRETNIHO maklere ("druhy dodavatel"). */
  readonly brokerUserId: string | null;
  readonly onChanged: () => Promise<void>;
  /** FIX7 nalez 2/9d - jen pri uprave JIZ AKTIVNIHO dodavatele (jinak se neda odejit bez ulozeni). */
  readonly onCancel?: (() => void) | undefined;
}): React.JSX.Element {
  const [form, setForm] = React.useState<CompanyForm>(
    existing === null
      ? EMPTY_COMPANY_FORM
      : {
          ico: existing.ico,
          companyName: existing.companyName,
          street: existing.street ?? '',
          city: existing.city ?? '',
          zip: existing.zip ?? '',
          dic: existing.dic ?? '',
          vatPayer: existing.vatPayer,
          email: existing.email ?? '',
        },
  );
  const [aresState, setAresState] = React.useState<'idle' | 'loading' | 'notFound' | 'error'>('idle');
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);

  async function lookupAres(): Promise<void> {
    if (!/^\d{8}$/.test(form.ico)) return;
    setAresState('loading');
    try {
      const company: AresCompanyDto | null = await lookupAresCompany(form.ico);
      if (company === null) {
        setAresState('notFound');
        return;
      }
      setForm((prev) => ({
        ...prev,
        companyName: company.companyName,
        street: company.street,
        city: company.city,
        zip: company.zip,
        // FIX6 nalez 9a - ARES DIC vyplni formular, aby platce DPH nemohl skoncit bez DIC na danovem dokladu.
        ...(company.dic !== null ? { dic: company.dic } : {}),
      }));
      setAresState('idle');
    } catch {
      setAresState('error');
    }
  }

  /**
   * Zivy pruchod 13. 10. (V6): pole „DIČ (nepovinné)" je graficky
   * nepovinne, ale backend ho pri zaskrtnutem „Plátce DPH" VYZADUJE —
   * formular spadal na obecnou 400 bez zvyrazneneho pole. Kontrola na
   * klientovi tomu predejde a hlasku dá k poli, ne jako obecny banner.
   */
  const dicRequired = form.vatPayer;
  const dicMissing = dicRequired && form.dic.trim() === '';

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (dicMissing) {
      setStatus('error');
      setErrorDetail(t('webAdmin.fakturace.settings.dicRequiredWhenVatPayer', { locale: LOCALE }));
      focusField('company-form-dic');
      return;
    }
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      await createOrRetrySupplier({
        companyName: form.companyName,
        ico: form.ico,
        vatPayer: form.vatPayer,
        street: form.street,
        city: form.city,
        zip: form.zip,
        email: form.email,
        ...(form.dic.trim() !== '' ? { dic: form.dic.trim() } : {}),
        ...(brokerUserId !== null ? { brokerUserId } : {}),
      });
      setStatus('saved');
      await onChanged();
    } catch (error) {
      setStatus('error');
      setErrorDetail(error instanceof DomainApiError ? error.message : undefined);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <FormSection
        title={t('webAdmin.fakturace.settings.companyHeading', { locale: LOCALE })}
        footer={
          <>
            <button type="submit" className="rh-btn" data-testid="company-form-submit">
              {t(
                existing?.status === 'error'
                  ? 'webAdmin.fakturace.settings.submitRetryButton'
                  : existing?.status === 'active'
                    ? 'webAdmin.fakturace.settings.submitUpdateButton'
                    : 'webAdmin.fakturace.settings.submitButton',
                { locale: LOCALE },
              )}
            </button>
            {onCancel !== undefined && (
              <button
                type="button"
                className="rh-btn"
                onClick={onCancel}
                data-testid="company-form-cancel"
              >
                {t('webAdmin.fakturace.settings.companyCancelButton', { locale: LOCALE })}
              </button>
            )}
            <SaveStatus
              status={status}
              texts={{
                saving: '…',
                saved: t(
                  existing?.status === 'active'
                    ? 'webAdmin.fakturace.settings.submitUpdateSuccess'
                    : 'webAdmin.fakturace.settings.submitSuccess',
                  { locale: LOCALE },
                ),
                error: '',
              }}
              {...errorDetailProp(errorDetail)}
            />
          </>
        }
      >
        {existing?.lastError !== null && existing?.lastError !== undefined && (
          <p role="alert" className="text-red-600">
            {existing.lastError}
          </p>
        )}
        <FieldRow label={t('webAdmin.fakturace.settings.icoLabel', { locale: LOCALE })} htmlFor="fak-ico">
          <div className="flex gap-2">
            <input id="fak-ico" required pattern="\d{8}" className="rh-input" value={form.ico} onChange={(e) => setForm((prev) => ({ ...prev, ico: e.target.value }))} data-testid="company-form-ico" />
            <button type="button" className="rh-btn" onClick={() => void lookupAres()} data-testid="company-form-ares-lookup">
              {t('webAdmin.fakturace.settings.icoLookupButton', { locale: LOCALE })}
            </button>
          </div>
          {aresState === 'notFound' && <p className="text-sm text-red-600">{t('webAdmin.fakturace.settings.icoLookupNotFound', { locale: LOCALE })}</p>}
          {aresState === 'error' && <p className="text-sm text-red-600">{t('webAdmin.fakturace.settings.icoLookupError', { locale: LOCALE })}</p>}
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.companyNameLabel', { locale: LOCALE })} htmlFor="fak-name">
          <input id="fak-name" required className="rh-input block" value={form.companyName} onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))} data-testid="company-form-name" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.streetLabel', { locale: LOCALE })} htmlFor="fak-street">
          <input id="fak-street" required className="rh-input block" value={form.street} onChange={(e) => setForm((prev) => ({ ...prev, street: e.target.value }))} data-testid="company-form-street" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.cityLabel', { locale: LOCALE })} htmlFor="fak-city">
          <input id="fak-city" required className="rh-input block" value={form.city} onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))} data-testid="company-form-city" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.zipLabel', { locale: LOCALE })} htmlFor="fak-zip">
          <input id="fak-zip" required className="rh-input block w-32" value={form.zip} onChange={(e) => setForm((prev) => ({ ...prev, zip: e.target.value }))} data-testid="company-form-zip" />
        </FieldRow>
        <FieldRow
          label={t(
            dicRequired
              ? 'webAdmin.fakturace.settings.dicLabelRequired'
              : 'webAdmin.fakturace.settings.dicLabel',
            { locale: LOCALE },
          )}
          htmlFor="fak-dic"
        >
          <input
            id="fak-dic"
            aria-required={dicRequired}
            className="rh-input block"
            value={form.dic}
            onChange={(e) => setForm((prev) => ({ ...prev, dic: e.target.value }))}
            data-testid="company-form-dic"
          />
          {status === 'error' && dicMissing && (
            <p className="rh-a2-error" role="alert" data-testid="company-form-dic-error">
              {t('webAdmin.fakturace.settings.dicRequiredWhenVatPayer', { locale: LOCALE })}
            </p>
          )}
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.vatPayerLabel', { locale: LOCALE })} htmlFor="fak-vat">
          <input id="fak-vat" type="checkbox" checked={form.vatPayer} onChange={(e) => setForm((prev) => ({ ...prev, vatPayer: e.target.checked }))} data-testid="company-form-vat-payer" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.emailLabel', { locale: LOCALE })} htmlFor="fak-email">
          <input id="fak-email" type="email" required className="rh-input block" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} data-testid="company-form-email" />
        </FieldRow>
      </FormSection>
    </form>
  );
}

/**
 * QR nahled vzorove platby na ucet dodavatele (zadani FAK1 bod 2). SPAYD text
 * pocita server (`bankAccountQrText` v @reos/finance) - tady se jen vykresluje.
 * Generator `qrcode` se stahuje az v efektu, stejne jako u stavu objednavky ve
 * `web-public` (krok C2b): je to jedina obrazovka adminu, ktera ho potrebuje.
 */
function BankQrPreview({ payload }: { readonly payload: string }): React.JSX.Element {
  const [dataUrl, setDataUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void import('qrcode')
      .then((mod) => mod.default.toDataURL(payload, { margin: 1, width: 180 }))
      .then(
        (url) => {
          if (!cancelled) setDataUrl(url);
        },
        () => {
          if (!cancelled) setDataUrl(null);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [payload]);

  return (
    <div data-testid="bank-qr-preview">
      <p className="text-sm text-gray-500">{t('webAdmin.fakturace.settings.bankQrTitle', { locale: LOCALE })}</p>
      {dataUrl !== null && (
        <img src={dataUrl} alt={t('webAdmin.fakturace.settings.bankQrAlt', { locale: LOCALE })} width={180} height={180} />
      )}
      <p className="break-all font-mono text-xs text-gray-500" data-testid="bank-qr-payload">
        {payload}
      </p>
      <p className="text-xs text-gray-500">{t('webAdmin.fakturace.settings.bankQrHint', { locale: LOCALE })}</p>
    </div>
  );
}

/**
 * Bankovni ucet. Krok FAK4: ukladani uz nekonci u REOS - ucet se rovnou zapisuje
 * k dodavateli v MyUctu a obrazovka rika, jestli to proslo. Dokud neproslo,
 * varuje nahlas: vystavena faktura by nesla JINY ucet (nalez 3 v zadani).
 */
function BankSection({
  supplier,
  qrText,
  syncJob,
  onChanged,
}: {
  readonly supplier: NonNullable<BillingOverviewDto['supplier']>;
  readonly qrText: string | null;
  readonly syncJob: SyncJobDto | undefined;
  readonly onChanged: () => Promise<void>;
}): React.JSX.Element {
  const initial = deliveryFromSyncJob(syncJob);
  const initialView = initial === null ? null : describeDelivery(initial, 'bank');
  const [account, setAccount] = React.useState(supplier.bankAccountIban ?? '');
  const [status, setStatus] = React.useState<SaveStatusKind>(initialView?.status ?? 'idle');
  const [savedText, setSavedText] = React.useState(initialView?.savedText ?? t('webAdmin.fakturace.settings.bankAccountSaved', { locale: LOCALE }));
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(initialView?.errorDetail);
  const [canRetry, setCanRetry] = React.useState(initialView?.canRetry ?? false);
  /** Po ulozeni je QR z odpovedi driv, nez dobehne `onChanged()` - nahled neceka na reload. */
  const [savedQrText, setSavedQrText] = React.useState<string | null>(null);
  const shownQrText = savedQrText ?? qrText;

  function applyDelivery(delivery: SyncDelivery): void {
    const view = describeDelivery(delivery, 'bank');
    setStatus(view.status);
    setSavedText(view.savedText);
    setErrorDetail(view.errorDetail);
    setCanRetry(view.canRetry);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      const result = await setSupplierBankAccount(supplier.id, account);
      setSavedQrText(result.qrText);
      applyDelivery(result.delivery);
      await onChanged();
    } catch (error) {
      setStatus('error');
      setCanRetry(false);
      setErrorDetail(errorMessage(error, 'webAdmin.fakturace.settings.bankAccountReasonShape'));
    }
  }

  async function retry(): Promise<void> {
    setStatus('saving');
    try {
      applyDelivery((await retrySupplierSync(supplier.id, 'bank')).delivery);
      await onChanged();
    } catch (error) {
      setStatus('error');
      setErrorDetail(errorMessage(error, 'webAdmin.fakturace.settings.bankDeliveryError'));
    }
  }

  const accountSaved = supplier.bankAccountIban !== null;

  return (
    <form onSubmit={(e) => void submit(e)}>
      <FormSection
        title={t('webAdmin.fakturace.settings.bankHeading', { locale: LOCALE })}
        footer={
          <>
            <button type="submit" className="rh-btn" data-testid="bank-form-submit">
              {t('webAdmin.fakturace.settings.bankAccountSaveButton', { locale: LOCALE })}
            </button>
            {canRetry && (
              <button type="button" className="rh-btn" onClick={() => void retry()} data-testid="bank-sync-retry">
                {t('webAdmin.fakturace.settings.bankRetryButton', { locale: LOCALE })}
              </button>
            )}
            <SaveStatus status={status} texts={{ saving: '…', saved: savedText, error: '' }} {...errorDetailProp(errorDetail)} />
          </>
        }
      >
        {supplier.bankAccountVerified && (
          <p className="text-sm text-green-700" data-testid="bank-verified-badge">
            ✓ {t('webAdmin.fakturace.settings.bankAccountSaved', { locale: LOCALE })}
          </p>
        )}
        {supplier.bankSynced && (
          <p className="text-sm text-green-700" data-testid="bank-synced-badge">
            ✓ {t('webAdmin.fakturace.settings.bankDeliveryApplied', { locale: LOCALE })}
          </p>
        )}
        {accountSaved && !supplier.bankSynced && (
          <p role="alert" className="text-sm text-red-600" data-testid="bank-not-synced-warning">
            {t('webAdmin.fakturace.settings.bankNotSyncedWarning', { locale: LOCALE })}
            {supplier.bankSyncError !== null && ` (${supplier.bankSyncError})`}
          </p>
        )}
        <FieldRow label={t('webAdmin.fakturace.settings.bankAccountLabel', { locale: LOCALE })} htmlFor="fak-bank" description={t('webAdmin.fakturace.settings.bankAccountHint', { locale: LOCALE })}>
          <input id="fak-bank" required className="rh-input block" value={account} onChange={(e) => setAccount(e.target.value)} data-testid="bank-form-input" />
          {/*
            Krok FIX6 bod 2: pole drzi IBAN, ale ucet, ktery firma zna a hleda
            na fakture, je narodni tvar („115-2595610287/0100") — ten v
            nastaveni chybel a pusobilo to, ze ucet v REOSu nikde neni (nalez
            ze ziveho overeni 2. 10.). Prepocet dela server, ne tahle sablona.
          */}
          {supplier.bankAccountLocal !== null && supplier.bankAccountLocal !== undefined && (
            <p className="rh-a2-meta" data-testid="bank-account-local">
              {t('webAdmin.fakturace.settings.bankAccountLocalLabel', {
                locale: LOCALE,
                values: { account: supplier.bankAccountLocal },
              })}
            </p>
          )}
        </FieldRow>
        {shownQrText !== null && <BankQrPreview payload={shownQrText} />}
      </FormSection>
    </form>
  );
}

interface BrandForm {
  businessName: string;
  accentColor: string | null;
  logoChoice: LogoChoice;
  tagline: string;
  footerText: string;
  inheritOfficeBrand: boolean;
}

function BrandSection({
  supplierId,
  brand,
  companyName,
  isBrokerSupplier,
  logo,
  inheritedFrom,
  officeLogoAvailable,
  syncJob,
  onChanged,
}: {
  readonly supplierId: string;
  readonly brand: BillingOverviewDto['brand'];
  readonly companyName: string;
  readonly isBrokerSupplier: boolean;
  readonly logo: SupplierLogoDto | null;
  readonly inheritedFrom: string | null;
  readonly officeLogoAvailable: boolean;
  readonly syncJob: SyncJobDto | undefined;
  readonly onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [form, setForm] = React.useState<BrandForm>({
    // Nazev na dokladu se od FAK4 UKLADA - pred nim se tu vzdy zobrazovalo
    // jmeno firmy, i kdyz uzivatel zadal neco jineho (nalez 2 v zadani).
    businessName: brand?.businessName ?? companyName,
    accentColor: brand?.accentColor ?? null,
    logoChoice: brand?.logoChoice ?? 'undecided',
    tagline: brand?.tagline ?? '',
    footerText: brand?.footerText ?? '',
    inheritOfficeBrand: brand?.inheritOfficeBrand ?? false,
  });
  const initial = deliveryFromSyncJob(syncJob);
  const initialView = initial === null ? null : describeDelivery(initial, 'brand');
  const [status, setStatus] = React.useState<SaveStatusKind>(initialView?.status ?? 'idle');
  const [savedText, setSavedText] = React.useState(initialView?.savedText ?? t('webAdmin.fakturace.settings.brandSaved', { locale: LOCALE }));
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(initialView?.errorDetail);
  const [canRetry, setCanRetry] = React.useState(initialView?.canRetry ?? false);

  function applyDelivery(delivery: SyncDelivery | null): void {
    if (delivery === null) {
      setStatus('saved');
      setSavedText(t('webAdmin.fakturace.settings.brandSaved', { locale: LOCALE }));
      setErrorDetail(undefined);
      setCanRetry(false);
      return;
    }
    const view = describeDelivery(delivery, 'brand');
    setStatus(view.status);
    setSavedText(view.savedText);
    setErrorDetail(view.errorDetail);
    setCanRetry(view.canRetry);
  }

  /** Spolecny obal pro vsechny akce sekce - jeden stav, jeden zpusob hlaseni chyby. */
  async function run(action: () => Promise<SyncDelivery | null>, fallbackKey: MessageKey): Promise<void> {
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      applyDelivery(await action());
      await onChanged();
    } catch (error) {
      setStatus('error');
      setCanRetry(false);
      setErrorDetail(errorMessage(error, fallbackKey));
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      const result = await updateSupplierBrand(supplierId, {
        accentColor: form.accentColor,
        logoChoice: form.logoChoice,
        colorsConfirmed: form.accentColor !== null,
        tagline: form.tagline.trim() === '' ? null : form.tagline.trim(),
        footerText: form.footerText.trim() === '' ? null : form.footerText.trim(),
        businessName: form.businessName.trim() === '' ? null : form.businessName.trim(),
        inheritOfficeBrand: form.inheritOfficeBrand,
      });
      return result.delivery;
    }, 'webAdmin.fakturace.settings.brandSaved');
  }

  async function pickLogo(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    await run(
      async () => (await uploadSupplierLogo(supplierId, await readFileAsBase64(file), file.name)).delivery,
      'webAdmin.fakturace.settings.brandLogoReasonFormat',
    );
  }

  const logoUrlKey = `${supplierId}:${logo?.byteSize ?? 0}:${logo?.filename ?? ''}`;

  const editor = (
    <form onSubmit={(e) => void submit(e)}>
      <FormSection
        title={t('webAdmin.fakturace.settings.brandHeading', { locale: LOCALE })}
        footer={
          <>
            <button type="submit" className="rh-btn" data-testid="brand-form-submit">
              {t('webAdmin.fakturace.settings.brandSaveButton', { locale: LOCALE })}
            </button>
            {canRetry && (
              <button
                type="button"
                className="rh-btn"
                onClick={() => void run(async () => (await retrySupplierSync(supplierId, 'brand')).delivery, 'webAdmin.fakturace.settings.brandSaved')}
                data-testid="brand-sync-retry"
              >
                {t('webAdmin.fakturace.settings.deliveryRetryButton', { locale: LOCALE })}
              </button>
            )}
            <SaveStatus status={status} texts={{ saving: '…', saved: savedText, error: '' }} {...errorDetailProp(errorDetail)} />
          </>
        }
      >
        {inheritedFrom !== null && (
          <p className="text-sm text-gray-600" data-testid="brand-inherited-notice">
            {t('webAdmin.fakturace.settings.brandInheritedNotice', { locale: LOCALE })}
          </p>
        )}
        <FieldRow label={t('webAdmin.fakturace.settings.brandBusinessNameLabel', { locale: LOCALE })} htmlFor="fak-brand-name">
          <input id="fak-brand-name" className="rh-input block" value={form.businessName} onChange={(e) => setForm((prev) => ({ ...prev, businessName: e.target.value }))} data-testid="brand-form-business-name" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.brandAccentColorLabel', { locale: LOCALE })} htmlFor="fak-brand-color">
          <input
            id="fak-brand-color"
            type="color"
            value={form.accentColor ?? '#3B2D83'}
            onChange={(e) => setForm((prev) => ({ ...prev, accentColor: e.target.value }))}
            data-testid="brand-form-color"
          />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.brandLogoChoiceLabel', { locale: LOCALE })} htmlFor="fak-brand-logo">
          <select
            id="fak-brand-logo"
            className="rh-input block"
            value={form.logoChoice}
            onChange={(e) => setForm((prev) => ({ ...prev, logoChoice: e.target.value as LogoChoice }))}
            data-testid="brand-form-logo-choice"
          >
            <option value="undecided">{t('webAdmin.fakturace.settings.brandLogoOptionUndecided', { locale: LOCALE })}</option>
            <option value="logo">{t('webAdmin.fakturace.settings.brandLogoOptionLogo', { locale: LOCALE })}</option>
            <option value="none">{t('webAdmin.fakturace.settings.brandLogoOptionNone', { locale: LOCALE })}</option>
          </select>
        </FieldRow>
        <FieldRow
          label={t('webAdmin.fakturace.settings.brandLogoUploadLabel', { locale: LOCALE })}
          htmlFor="fak-brand-logo-file"
          description={t('webAdmin.fakturace.settings.brandLogoUploadHint', { locale: LOCALE })}
        >
          {/*
            Pri dedenem brandu je logo na dokladu logo KANCELARE - nahrat ani
            odebrat ho odsud nejde (sahlo by to na dodavatele maklere, jehoz
            logo se stejne nepouzije). Nahled zustava: ukazuje, co bude na
            dokladu.
          */}
          {inheritedFrom === null && (
            <>
              <input
                id="fak-brand-logo-file"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="block"
                onChange={(e) => void pickLogo(e)}
                data-testid="brand-logo-file"
              />
              <div className="mt-2 flex gap-2">
                {officeLogoAvailable && (
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => void run(async () => (await useOfficeLogo(supplierId)).delivery, 'webAdmin.fakturace.settings.brandLogoOfficeUnavailable')}
                    data-testid="brand-logo-from-office"
                  >
                    {t('webAdmin.fakturace.settings.brandLogoFromOfficeButton', { locale: LOCALE })}
                  </button>
                )}
                {logo !== null && (
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => void run(async () => (await removeSupplierLogo(supplierId)).delivery, 'webAdmin.fakturace.settings.brandLogoNone')}
                    data-testid="brand-logo-remove"
                  >
                    {t('webAdmin.fakturace.settings.brandLogoRemoveButton', { locale: LOCALE })}
                  </button>
                )}
              </div>
            </>
          )}
          <LogoPreview supplierId={supplierId} logo={logo} cacheKey={logoUrlKey} />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.brandTaglineLabel', { locale: LOCALE })} htmlFor="fak-brand-tagline">
          <input id="fak-brand-tagline" className="rh-input block" value={form.tagline} onChange={(e) => setForm((prev) => ({ ...prev, tagline: e.target.value }))} data-testid="brand-form-tagline" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturace.settings.brandFooterLabel', { locale: LOCALE })} htmlFor="fak-brand-footer">
          <input id="fak-brand-footer" className="rh-input block" value={form.footerText} onChange={(e) => setForm((prev) => ({ ...prev, footerText: e.target.value }))} data-testid="brand-form-footer" />
        </FieldRow>
        {isBrokerSupplier && (
          <FieldRow
            label={t('webAdmin.fakturace.settings.brandInheritLabel', { locale: LOCALE })}
            htmlFor="fak-brand-inherit"
            description={t('webAdmin.fakturace.settings.brandInheritHint', { locale: LOCALE })}
          >
            <input
              id="fak-brand-inherit"
              type="checkbox"
              checked={form.inheritOfficeBrand}
              onChange={(e) => setForm((prev) => ({ ...prev, inheritOfficeBrand: e.target.checked }))}
              data-testid="brand-form-inherit-office"
            />
          </FieldRow>
        )}
      </FormSection>
    </form>
  );

  return (
    <SplitEditorLayout
      editor={editor}
      preview={<InvoicePdfPreview supplierId={supplierId} />}
      mobileEditLabel={t('webAdmin.fakturace.settings.brandHeading', { locale: LOCALE })}
      mobilePreviewLabel={t('webAdmin.fakturace.settings.previewTitle', { locale: LOCALE })}
    />
  );
}

/**
 * Logo tak, jak ho uvidi doklad. Bajty jdou pres autorizovany endpoint
 * (`credentials: 'include'`), takze `<img src>` na URL nestaci - stahuje se
 * `fetch`em do blob URL. `cacheKey` zmena vynuti nove stazeni po nahrani.
 */
function LogoPreview({
  supplierId,
  logo,
  cacheKey,
}: {
  readonly supplierId: string;
  readonly logo: SupplierLogoDto | null;
  readonly cacheKey: string;
}): React.JSX.Element {
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  // Zamerne `logo !== null`, ne `logo`: overview se prekresluje po kazde akci
  // a zavislost na IDENTITE objektu by logo stahovala znovu bez duvodu.
  const hasLogo = logo !== null;

  React.useEffect(() => {
    if (!hasLogo) {
      setObjectUrl(null);
      return undefined;
    }
    let url: string | null = null;
    let cancelled = false;
    void fetchSupplierLogo(supplierId).then(
      (blob) => {
        if (cancelled || blob === null) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      },
      () => setObjectUrl(null),
    );
    return () => {
      cancelled = true;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [supplierId, hasLogo, cacheKey]);

  if (logo === null) {
    return (
      <p className="text-sm text-gray-500" data-testid="brand-logo-none">
        {t('webAdmin.fakturace.settings.brandLogoNone', { locale: LOCALE })}
      </p>
    );
  }

  return (
    <div className="mt-2" data-testid="brand-logo-preview">
      <p className="text-sm text-gray-500">{t('webAdmin.fakturace.settings.brandLogoCurrentLabel', { locale: LOCALE })}</p>
      {objectUrl !== null && (
        <img src={objectUrl} alt={t('webAdmin.fakturace.settings.brandLogoAlt', { locale: LOCALE })} className="max-h-16" />
      )}
      <p className="text-xs text-gray-500" data-testid="brand-logo-source">
        {t(
          logo.source === 'office' ? 'webAdmin.fakturace.settings.brandLogoSourceOffice' : 'webAdmin.fakturace.settings.brandLogoSourceUpload',
          { locale: LOCALE },
        )}
      </p>
    </div>
  );
}

/**
 * Nahled dokladu = SKUTECNE PDF z MyUcta (zadani FAK4 bod 4). Do FAK4 tu byla
 * HTML atrapa se slovem "LOGO", ktera ukazovala, co si o vzhledu mysli REOS -
 * ne co firma dostane. Tahne se az na vyzadani: kazdy nahled je draft v enginu
 * (a hned se maze), takze se nesmi delat pri kazdem preklepu ve formulari.
 */
function InvoicePdfPreview({ supplierId }: { readonly supplierId: string }): React.JSX.Element {
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => () => {
    if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const blob = await fetchSupplierPreviewPdf(supplierId);
      setObjectUrl(URL.createObjectURL(blob));
    } catch {
      setError(t('webAdmin.fakturace.settings.previewError', { locale: LOCALE }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rh-card p-4" data-testid="invoice-pdf-preview">
      <p className="text-sm font-semibold">{t('webAdmin.fakturace.settings.previewTitle', { locale: LOCALE })}</p>
      <p className="mb-2 text-xs text-gray-600">{t('webAdmin.fakturace.settings.previewHint', { locale: LOCALE })}</p>
      <button type="button" className="rh-btn" onClick={() => void load()} disabled={loading} data-testid="invoice-preview-load">
        {t('webAdmin.fakturace.settings.previewLoadButton', { locale: LOCALE })}
      </button>
      {loading && <p role="status">…</p>}
      {error !== null && (
        <p role="alert" className="text-sm text-red-600" data-testid="invoice-preview-error">
          {error}
        </p>
      )}
      {objectUrl !== null && (
        <>
          <iframe
            src={objectUrl}
            title={t('webAdmin.fakturace.settings.previewIframeTitle', { locale: LOCALE })}
            className="mt-2 h-96 w-full border"
            data-testid="invoice-preview-frame"
          />
          <a href={objectUrl} target="_blank" rel="noreferrer" className="text-sm underline">
            {t('webAdmin.fakturace.settings.previewDownloadLink', { locale: LOCALE })}
          </a>
        </>
      )}
    </div>
  );
}

/**
 * Krok FAK5 bod 3 - "druhy dodavatel": makler OSVC v teze kancelari fakturuje
 * sam za sebe (vlastni dodavatel vedle dodavatele kancelare, FAK1 uz tohle
 * umel na urovni service/API - `createOrRetrySupplier` s explicitnim
 * `brokerUserId` smi jen SUPPLIER_MANAGE_ROLES - tady jen pribyva cesta z UI).
 * Makler sam (bez spravnich prav) na `listSuppliers()` dostane 403 - sekce se
 * pro nej tise schova, zadna zvlastni "nemas opravneni" hlaska.
 */
function SecondSupplierSection(): React.JSX.Element | null {
  const [suppliers, setSuppliers] = React.useState<SupplierDto[] | null>(null);
  const [members, setMembers] = React.useState<readonly TenantMember[]>([]);
  const [forbidden, setForbidden] = React.useState(false);
  const [selectedBrokerUserId, setSelectedBrokerUserId] = React.useState<string | null>(null);
  const [newBrokerUserId, setNewBrokerUserId] = React.useState('');

  const reload = React.useCallback(async () => {
    try {
      const [s, m] = await Promise.all([listSuppliers(), listMembers()]);
      setSuppliers(s);
      setMembers(m);
    } catch (error) {
      if (error instanceof DomainApiError && error.status === 403) {
        setForbidden(true);
        return;
      }
      throw error;
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  if (forbidden || suppliers === null) return null;

  const brokerSuppliers = suppliers.filter((s) => s.brokerUserId !== null);
  const membersWithoutSupplier = members.filter((m) => !brokerSuppliers.some((s) => s.brokerUserId === m.userId));

  return (
    <FormSection
      title={t('webAdmin.fakturace.settings.secondSupplierHeading', { locale: LOCALE })}
    >
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.fakturace.settings.secondSupplierDetail', { locale: LOCALE })}</p>

      {brokerSuppliers.length === 0 ? (
        <p data-testid="second-supplier-empty">{t('webAdmin.fakturace.settings.secondSupplierEmpty', { locale: LOCALE })}</p>
      ) : (
        <table className="mb-3 w-full text-sm" data-testid="second-supplier-table">
          <tbody>
            {brokerSuppliers.map((supplier) => {
              const member = members.find((m) => m.userId === supplier.brokerUserId);
              return (
                <tr key={supplier.id} data-testid={`second-supplier-row-${supplier.id}`}>
                  <td>{member?.name ?? supplier.brokerUserId}</td>
                  <td>
                    {supplier.companyName} — {supplier.ico}
                    {/* FIX6 nalez 9c: chyba provisioningu driv nikde videt nebyla - radek vypadal uplne normalne. */}
                    {supplier.status === 'error' && (
                      <span className="rh-badge rh-badge--bad ml-2" data-testid={`second-supplier-error-${supplier.id}`}>
                        {t('webAdmin.fakturace.settings.secondSupplierErrorBadge', { locale: LOCALE })}
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="rh-btn"
                      onClick={() => setSelectedBrokerUserId(supplier.brokerUserId)}
                      data-testid={`second-supplier-edit-${supplier.id}`}
                    >
                      {t('webAdmin.fakturace.settings.secondSupplierEditButton', { locale: LOCALE })}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {membersWithoutSupplier.length > 0 && (
        <FieldRow label={t('webAdmin.fakturace.settings.secondSupplierPickLabel', { locale: LOCALE })} htmlFor="second-supplier-pick">
          <select
            id="second-supplier-pick"
            className="rh-input block"
            value={newBrokerUserId}
            onChange={(e) => setNewBrokerUserId(e.target.value)}
            data-testid="second-supplier-pick"
          >
            <option value="">{t('webAdmin.fakturace.settings.secondSupplierPickPlaceholder', { locale: LOCALE })}</option>
            {membersWithoutSupplier.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </select>
        </FieldRow>
      )}

      {newBrokerUserId !== '' && (
        <CompanySection
          existing={null}
          brokerUserId={newBrokerUserId}
          onChanged={async () => {
            setNewBrokerUserId('');
            await reload();
          }}
        />
      )}

      {selectedBrokerUserId !== null && (
        <BrokerSupplierEditor brokerUserId={selectedBrokerUserId} onChanged={reload} />
      )}
    </FormSection>
  );
}

/** Bank/brand editor konkretniho dodavatele maklere - stejne sekce jako v hlavnim `SettingsTab`, jen nad overview NAMIROVANYM na toho maklere. */
function BrokerSupplierEditor({ brokerUserId, onChanged }: { readonly brokerUserId: string; readonly onChanged: () => Promise<void> }): React.JSX.Element | null {
  const [overview, setOverview] = React.useState<BillingOverviewDto | null>(null);

  const reload = React.useCallback(async () => {
    setOverview(await getBillingOverview(brokerUserId));
  }, [brokerUserId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  async function combinedOnChanged(): Promise<void> {
    await reload();
    await onChanged();
  }

  if (overview === null) return <p role="status">…</p>;
  if (overview.supplier === null) return null;

  // FIX6 nalez 9c: dodavatel makléře ve stavu "error" driv skakal rovnou na
  // ucet/vzhled (ktery bez `myucto_supplier_id` nema smysl) - zadna chyba,
  // zadne tlacitko "Zkusit znovu". `CompanySection` uz retry umi (stejna
  // cesta jako dodavatel kanceláře, `createOrRetrySupplier`), jen se sem
  // nikdy nedostala.
  if (overview.supplier.status === 'error') {
    return (
      <div data-testid="second-supplier-editor">
        <CompanySection existing={overview.supplier} brokerUserId={brokerUserId} onChanged={combinedOnChanged} />
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-4" data-testid="second-supplier-editor">
      <BankSection
        supplier={overview.supplier}
        qrText={overview.bankQrText}
        syncJob={overview.syncJobs.find((job) => job.kind === 'bank')}
        onChanged={combinedOnChanged}
      />
      <BrandSection
        supplierId={overview.supplier.id}
        brand={overview.brand}
        companyName={overview.supplier.companyName}
        isBrokerSupplier
        logo={overview.logo}
        inheritedFrom={overview.brandInheritedFrom}
        officeLogoAvailable={overview.officeLogoAvailable}
        syncJob={overview.syncJobs.find((job) => job.kind === 'brand')}
        onChanged={combinedOnChanged}
      />
    </div>
  );
}
